import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import SectionHeading from "@/components/base/SectionHeading";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import type { Work, NewsItem } from "@/types";
import HeroCarousel from "./components/HeroCarousel";
import { works as rawWorks } from "@/mocks/works";
import { news as rawNews } from "@/mocks/news";
import { profile } from "@/mocks/profile";

export default function Home() {
  useDocumentMeta({
    title: "舒淇明星官网 - 影视作品、个人资料与最新动态",
    description:
      "舒淇官网致力于提供舒淇的详细个人资料、丰富影视作品介绍和最新动态资讯，满足广大影迷的观看需求。",
    keywords: "舒淇,舒淇影视作品,舒淇个人资料,舒淇动态,舒淇明星",
  });

  const works = rawWorks as unknown as Work[];
  const news = rawNews as unknown as NewsItem[];

  const featured = works.filter((w) => w.featured).slice(0, 8);
  const latestNews = [...news]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 3);

  return (
    <div>
      <HeroCarousel />

      {/* 精选作品 */}
      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeading
            eyebrow="Featured Works"
            title="影视作品精选"
            description="从文艺经典到商业巨制，精选舒淇最具代表性的银幕作品。"
          />
          <Link
            to="/works"
            className="inline-flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 whitespace-nowrap cursor-pointer"
          >
            查看全部作品
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      {/* 最新动态 */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHeading
              eyebrow="Latest News"
              title="最新动态速递"
              description="第一时间了解舒淇的最新活动、作品消息与专访内容。"
            />
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 whitespace-nowrap cursor-pointer"
            >
              更多资讯
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {latestNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* 关于舒淇 */}
      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
            <img
              src={profile.avatar}
              alt="舒淇 个人资料写真"
              title="舒淇 个人资料"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-background-50/90 backdrop-blur-sm rounded-lg p-4">
              <p className="font-heading text-lg font-semibold text-foreground-950">
                舒淇 · Shu Qi
              </p>
              <p className="text-xs text-secondary-500 mt-1">
                演员 / 模特 · 华语影坛重要代表人物
              </p>
            </div>
          </div>

          <div>
            <SectionHeading
              eyebrow="About Shu Qi"
              title="关于舒淇"
              description={profile.intro}
            />
            <div className="mt-8 grid grid-cols-2 gap-4">
              {profile.facts.map((fact) => (
                <div
                  key={fact.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <p className="text-xs text-secondary-500">{fact.label}</p>
                  <p className="mt-1 font-heading text-base font-semibold text-foreground-950">
                    {fact.value}
                  </p>
                </div>
              ))}
            </div>
            <Link
              to="/profile"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看完整资料
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=a%20minimal%20elegant%20dark%20stage%20with%20soft%20warm%20spotlight%20and%20subtle%20curtain%20texture%20cinematic%20wide%20composition%20moody%20dramatic%20lighting%20soft%20film%20grain%20warm%20neutral%20and%20rose%20tones%20high%20detail&width=1920&height=700&seq=home-cta-banner&orientation=landscape"
            alt="舒淇影视作品专题"
            title="舒淇影视作品专题"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/75" />
        </div>
        <div className="relative z-10 max-w-content mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
            走进舒淇的光影世界
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-100/85 max-w-2xl mx-auto leading-relaxed">
            从影多年，舒淇以细腻而有力的表演塑造了众多经典形象。浏览作品大全，
            重温那些令人难忘的银幕瞬间。
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/works"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-film-line" />
              浏览影视作品
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看最新动态
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}